import React, {
    createContext,
    FC,
    PropsWithChildren,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { getKeycloakInstance } from '../services';
import { KeycloakToken, User } from '../types';

import {
    setRealmRoles,
    setUser as setKeycloakUser,
    unsetUser,
} from './authSlice';

export interface AuthContextProps {
    isAuthenticated: boolean | undefined;
    user: User | null;
    login: () => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
    isAuthenticated: false,
    user: null,
    login: () => {},
    logout: () => {},
});

type AuthProviderProps = {
    keycloakUrl: string;
    keycloakRealm: string;
    keycloakClientId: string;
    redirectIfUnauthenticated?: boolean;
};

export const AuthProvider: FC<PropsWithChildren<AuthProviderProps>> = ({
    keycloakUrl,
    keycloakRealm,
    keycloakClientId,
    children,
    redirectIfUnauthenticated = true,
}) => {
    const dispatch = useDispatch();

    const keycloakService = useRef(
        getKeycloakInstance(keycloakUrl, keycloakRealm, keycloakClientId),
    ).current;

    const [isAuthenticated, setIsAuthenticated] = useState<
        boolean | undefined
    >();
    const [user, setUser] = useState<User | null>(null);

    const isInitialized = useRef(false);

    const initKeycloak = async () => {
        try {
            if (isInitialized.current) return;
            isInitialized.current = true;

            const handleLogout = () => {
                setIsAuthenticated(false);
                setUser(null);
                dispatch(unsetUser());
            };

            keycloakService.onTokenExpired = () => {
                keycloakService.logout();
                handleLogout();
            };
            keycloakService.onAuthRefreshError = () => {
                keycloakService.logout();
                handleLogout();
            };
            keycloakService.onAuthLogout = handleLogout;

            const authenticated = await keycloakService.init({
                onLoad: redirectIfUnauthenticated
                    ? 'login-required'
                    : 'check-sso',
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                checkLoginIframe: true,
                flow: 'standard',
                pkceMethod: 'S256',
            });

            setIsAuthenticated(authenticated);

            const tokenParsed = keycloakService.tokenParsed as KeycloakToken;
            if (authenticated && tokenParsed) {
                const { user } = tokenParsed;

                setUser(user);

                dispatch(setKeycloakUser({ user }));
                dispatch(
                    setRealmRoles({
                        roles: tokenParsed?.resource_access['realm-management']
                            .roles,
                    }),
                );
            } else {
                dispatch(unsetUser());
            }
        } catch (error) {
            // TODO: handle error
            setIsAuthenticated(false);
            return;
        }
    };

    useEffect(() => {
        initKeycloak();

        return () => {
            keycloakService.onTokenExpired = undefined;
            keycloakService.onAuthRefreshError = undefined;
            keycloakService.onAuthLogout = undefined;
        };
    }, []);

    // TODO: loading spinner?
    if (isAuthenticated === undefined) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                login: () => keycloakService.login(),
                logout: () => keycloakService.logout(),
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
